import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Shield } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TID } from "@/lib/testIds";
import { toast } from "sonner";

const ALLOWED_DOMAIN = "virtusmaritime.com";

export default function Login() {
    const [stage, setStage] = useState("email"); // email | code | pin | setup-pin
    const [email, setEmail] = useState("");
    const [code, setCode] = useState("");
    const [pin, setPin] = useState("");
    const [loading, setLoading] = useState(false);
    const [hasPin, setHasPin] = useState(false);
    const { login, user, refresh, setUser } = useAuth();
    const nav = useNavigate();

    useEffect(() => {
        // Don't yank the user off the setup-pin screen — they haven't finished
        // that optional step yet. All other stages should bounce to /hub once
        // a session is live.
        if (user && stage !== "setup-pin") nav("/hub");
    }, [user, nav, stage]);

    const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    const emailDomainOK = email.toLowerCase().endsWith(`@${ALLOWED_DOMAIN}`);

    const requestCode = async (opts = {}) => {
        const { bypassPinShortcut = false } = opts;
        if (!emailValid) return toast.error("Enter a valid email address.");
        if (!emailDomainOK) return toast.error(`Access is restricted to @${ALLOWED_DOMAIN}.`);
        setLoading(true);
        try {
            if (!bypassPinShortcut) {
                // Check if user already has PIN for shortcut
                const { data: st } = await api.get(`/auth/pin-status?email=${encodeURIComponent(email)}`);
                if (st.has_pin) {
                    setHasPin(true);
                    setStage("pin");
                    setLoading(false);
                    return;
                }
            }
            await api.post("/auth/request-code", { email });
            toast.success("Verification code dispatched.");
            setStage("code");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to send code.");
        } finally {
            setLoading(false);
        }
    };

    const verifyCode = async () => {
        if (code.length !== 6) return toast.error("Enter the 6-digit code.");
        setLoading(true);
        try {
            const { data } = await api.post("/auth/verify-code", { email, code });
            login(data.token, data.user);
            if (!data.user.has_pin) {
                setStage("setup-pin");
            } else {
                nav("/hub");
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || "Verification failed.");
        } finally {
            setLoading(false);
        }
    };

    const pinLogin = async () => {
        if (pin.length < 4) return toast.error("Enter your PIN.");
        setLoading(true);
        try {
            const { data } = await api.post("/auth/pin-login", { email, pin });
            login(data.token, data.user);
            nav("/hub");
        } catch (e) {
            toast.error(e.response?.data?.detail || "PIN login failed.");
        } finally {
            setLoading(false);
        }
    };

    const savePin = async () => {
        if (pin.length < 4) return toast.error("PIN must be 4-6 digits.");
        setLoading(true);
        try {
            await api.post("/auth/set-pin", { pin });
            await refresh();
            toast.success("PIN configured.");
            nav("/hub");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not save PIN.");
        } finally {
            setLoading(false);
        }
    };

    const skipPin = () => nav("/hub");

    return (
        <div className="relative min-h-screen bg-virtus-bg text-white overflow-hidden">
            <div className="absolute inset-0 hud-grid opacity-40" />
            {/* Corner brackets */}
            <div className="absolute top-6 left-6 w-6 h-6 border-l-2 border-t-2 border-virtus-orange" />
            <div className="absolute top-6 right-6 w-6 h-6 border-r-2 border-t-2 border-virtus-orange" />
            <div className="absolute bottom-6 left-6 w-6 h-6 border-l-2 border-b-2 border-virtus-orange" />
            <div className="absolute bottom-6 right-6 w-6 h-6 border-r-2 border-b-2 border-virtus-orange" />

            <div className="relative z-10 min-h-screen grid grid-cols-1 lg:grid-cols-2">
                {/* LEFT: brand pane (desktop). On mobile we render a compact
                    header ribbon at the top of the form instead so the logo
                    doesn't take up the whole screen. */}
                <div className="hidden lg:flex flex-col justify-between p-14 border-r border-virtus-line">
                    <div className="flex items-center gap-3">
                        <img
                            src="/logos/virtus-white.png"
                            alt="VIRTUS"
                            className="h-9 w-auto"
                        />
                        <div className="h-6 w-px bg-virtus-line" />
                        <div className="font-mono text-[11px] tracking-[0.32em] uppercase text-virtus-orange">
                            OPERATIONS CENTER
                        </div>
                    </div>
                    <div>
                        <div className="font-mono text-[10px] tracking-[0.32em] uppercase text-virtus-dim mb-6">
                            OPERATIONAL COMMAND SYSTEM // v1.0
                        </div>
                        <img
                            src="/logos/virtus-maritime-white.png"
                            alt="VIRTUS MARITIME"
                            className="w-full max-w-[520px] h-auto opacity-95 mb-8"
                        />
                        <div className="max-w-[520px]">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="h-px w-8 bg-virtus-orange" />
                                <div className="font-mono text-[10px] tracking-[0.36em] uppercase text-virtus-orange">
                                    // OPERATING TENET
                                </div>
                            </div>
                            <div className="relative pl-5 border-l-2 border-virtus-orange">
                                <p className="font-heading text-2xl md:text-[28px] font-semibold text-white leading-[1.15] tracking-tight">
                                    As effective as possible.
                                </p>
                                <p className="font-heading text-2xl md:text-[28px] font-semibold text-white/55 leading-[1.15] tracking-tight mt-1">
                                    As complex as necessary.
                                </p>
                            </div>
                        </div>
                    </div>
                    <div className="grid grid-cols-3 gap-8 font-mono text-[10px] tracking-[0.28em] uppercase text-virtus-faint">
                        <div>
                            <div className="text-virtus-orange text-lg font-bold">08</div>
                            FOCUS AREAS
                        </div>
                        <div>
                            <div className="text-virtus-orange text-lg font-bold">24/7</div>
                            ACTIVE MONITORING
                        </div>
                        <div>
                            <div className="text-virtus-orange text-lg font-bold">VOX</div>
                            "HEY VIRTUS"
                        </div>
                    </div>
                </div>

                {/* Mobile-only compact brand ribbon */}
                <div className="lg:hidden flex flex-col items-center justify-center px-6 pt-10 pb-4">
                    <img
                        src="/logos/virtus-white.png"
                        alt="VIRTUS"
                        className="h-10 w-auto mb-3"
                    />
                    <div className="font-mono text-[9px] tracking-[0.32em] uppercase text-virtus-orange">
                        OPERATIONS CENTER
                    </div>
                    <div className="mt-4 relative pl-4 border-l-2 border-virtus-orange">
                        <p className="font-heading text-base font-semibold text-white leading-tight tracking-tight">
                            As effective as possible.
                        </p>
                        <p className="font-heading text-base font-semibold text-white/55 leading-tight tracking-tight">
                            As complex as necessary.
                        </p>
                    </div>
                </div>

                {/* RIGHT: form pane */}
                <div className="flex flex-col justify-center px-6 py-8 lg:p-14">
                    <motion.div
                        key={stage}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.2 }}
                        className="w-full max-w-md mx-auto"
                    >
                        {stage !== "email" && (
                            <button
                                onClick={() => setStage("email")}
                                className="flex items-center gap-2 font-mono text-[10px] tracking-[0.28em] uppercase text-virtus-dim hover:text-virtus-orange mb-6"
                                data-testid={TID.loginBackBtn}
                            >
                                <ArrowLeft size={12} /> BACK
                            </button>
                        )}

                        {stage === "email" && (
                            <>
                                <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-virtus-orange mb-3">
                                    STEP 01 // AUTHENTICATION
                                </div>
                                <h2 className="font-heading text-4xl font-black uppercase tracking-tight mb-8">
                                    Identify yourself.
                                </h2>
                                <label className="font-mono text-[10px] tracking-[0.22em] uppercase text-virtus-dim">
                                    OPERATOR EMAIL
                                </label>
                                <input
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    onKeyDown={(e) => e.key === "Enter" && requestCode()}
                                    placeholder={`name@${ALLOWED_DOMAIN}`}
                                    className="mt-2 w-full bg-transparent border border-virtus-line focus:border-virtus-orange px-4 py-3 text-white outline-none"
                                    data-testid={TID.loginEmailInput}
                                    autoFocus
                                />
                                <p className="mt-3 font-mono text-[10px] tracking-wide text-virtus-faint uppercase">
                                    Access restricted to @{ALLOWED_DOMAIN}
                                </p>
                                <button
                                    onClick={requestCode}
                                    disabled={loading}
                                    className="mt-8 w-full flex items-center justify-center gap-3 bg-virtus-orange text-black font-mono text-[11px] tracking-[0.28em] uppercase font-bold py-4 hover:bg-white transition-colors glow-orange disabled:opacity-60"
                                    data-testid={TID.loginRequestCodeBtn}
                                >
                                    {loading ? "REQUESTING..." : "REQUEST ACCESS CODE"}
                                    <ArrowRight size={14} />
                                </button>
                            </>
                        )}

                        {stage === "code" && (
                            <>
                                <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-virtus-orange mb-3">
                                    STEP 02 // VERIFY
                                </div>
                                <h2 className="font-heading text-4xl font-black uppercase tracking-tight mb-2">
                                    Enter access code.
                                </h2>
                                <p className="text-sm text-virtus-dim mb-4">
                                    We sent a 6-digit code to{" "}
                                    <span className="text-virtus-orange font-mono">{email}</span>
                                </p>
                                <input
                                    value={code}
                                    onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                                    onKeyDown={(e) => e.key === "Enter" && verifyCode()}
                                    placeholder="000000"
                                    className="w-full bg-transparent border border-virtus-line focus:border-virtus-orange px-4 py-4 text-white outline-none text-center text-3xl font-mono tracking-[0.6em]"
                                    data-testid={TID.loginCodeInput}
                                    autoFocus
                                />
                                <button
                                    onClick={verifyCode}
                                    disabled={loading}
                                    className="mt-8 w-full flex items-center justify-center gap-3 bg-virtus-orange text-black font-mono text-[11px] tracking-[0.28em] uppercase font-bold py-4 hover:bg-white transition-colors glow-orange disabled:opacity-60"
                                    data-testid={TID.loginVerifyBtn}
                                >
                                    {loading ? "VERIFYING..." : "VERIFY & ENTER"}
                                    <Shield size={14} />
                                </button>
                            </>
                        )}

                        {stage === "pin" && (
                            <>
                                <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-virtus-orange mb-3">
                                    QUICK ACCESS // PIN
                                </div>
                                <h2 className="font-heading text-4xl font-black uppercase tracking-tight mb-2">
                                    Enter your PIN.
                                </h2>
                                <p className="text-sm text-virtus-dim mb-8">
                                    {email}. Or{" "}
                                    <button
                                        onClick={() => requestCode({ bypassPinShortcut: true })}
                                        className="text-virtus-orange underline"
                                        data-testid="login-request-code-instead-btn"
                                    >
                                        request an email code instead
                                    </button>
                                    .
                                </p>
                                <input
                                    value={pin}
                                    type="password"
                                    inputMode="numeric"
                                    onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
                                    onKeyDown={(e) => e.key === "Enter" && pinLogin()}
                                    placeholder="••••"
                                    className="w-full bg-transparent border border-virtus-line focus:border-virtus-orange px-4 py-4 text-white outline-none text-center text-3xl font-mono tracking-[0.6em]"
                                    data-testid={TID.loginPinInput}
                                    autoFocus
                                />
                                <button
                                    onClick={pinLogin}
                                    disabled={loading}
                                    className="mt-8 w-full flex items-center justify-center gap-3 bg-virtus-orange text-black font-mono text-[11px] tracking-[0.28em] uppercase font-bold py-4 hover:bg-white transition-colors glow-orange disabled:opacity-60"
                                    data-testid={TID.loginPinBtn}
                                >
                                    {loading ? "UNLOCKING..." : "UNLOCK"}
                                    <ArrowRight size={14} />
                                </button>
                            </>
                        )}

                        {stage === "setup-pin" && (
                            <>
                                <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-virtus-orange mb-3">
                                    OPTIONAL // QUICK ACCESS
                                </div>
                                <h2 className="font-heading text-4xl font-black uppercase tracking-tight mb-2">
                                    Set a PIN?
                                </h2>
                                <p className="text-sm text-virtus-dim mb-8">
                                    Configure a 4-6 digit PIN for faster subsequent logins from
                                    this station. You can skip this and always use email codes.
                                </p>
                                <input
                                    value={pin}
                                    type="password"
                                    inputMode="numeric"
                                    onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
                                    onKeyDown={(e) => e.key === "Enter" && savePin()}
                                    placeholder="••••"
                                    className="w-full bg-transparent border border-virtus-line focus:border-virtus-orange px-4 py-4 text-white outline-none text-center text-3xl font-mono tracking-[0.6em]"
                                    data-testid={TID.setPinInput}
                                    autoFocus
                                />
                                <div className="mt-8 flex gap-3">
                                    <button
                                        onClick={skipPin}
                                        className="flex-1 font-mono text-[11px] tracking-[0.28em] uppercase font-bold py-4 border border-virtus-line text-virtus-dim hover:text-white hover:border-white"
                                        data-testid={TID.skipPinBtn}
                                    >
                                        SKIP
                                    </button>
                                    <button
                                        onClick={savePin}
                                        disabled={loading}
                                        className="flex-1 bg-virtus-orange text-black font-mono text-[11px] tracking-[0.28em] uppercase font-bold py-4 hover:bg-white glow-orange disabled:opacity-60"
                                        data-testid={TID.setPinBtn}
                                    >
                                        SAVE PIN
                                    </button>
                                </div>
                            </>
                        )}
                    </motion.div>
                </div>
            </div>
        </div>
    );
}
